import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { GraduationCap, Plus, Trash2, Users } from "lucide-react";

export default function Classes() {
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ grade: "", branch: "", advisor: "" });

  const load = () => api.get("/classes").then((r) => setClasses(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.grade || !form.branch) { toast.error("Sınıf ve şube gerekli"); return; }
    try { await api.post("/classes", form); toast.success("Sınıf eklendi"); setOpen(false); setForm({ grade: "", branch: "", advisor: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/classes/${id}`); toast.success("Sınıf silindi"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <PageHeader title="Sınıflar & Şubeler" desc="Sınıf ve şubeleri yönetin">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="class-add"><Plus className="h-4 w-4 mr-2" />Sınıf / Şube Ekle</Button></DialogTrigger>
          <DialogContent className="bg-card border-border max-w-sm">
            <DialogHeader><DialogTitle>Yeni Sınıf / Şube</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Sınıf</Label><Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="8" data-testid="class-grade" /></div>
                <div className="space-y-1.5"><Label>Şube</Label><Input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value.toUpperCase() })} placeholder="A" data-testid="class-branch" /></div>
              </div>
              <div className="space-y-1.5"><Label>Rehber Öğretmen</Label><Input value={form.advisor} onChange={(e) => setForm({ ...form, advisor: e.target.value })} data-testid="class-advisor" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button><Button onClick={save} data-testid="class-save">Kaydet</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {classes.length === 0 ? (
        <EmptyState icon={GraduationCap} title="Henüz sınıf yok" desc="Öğrenci eklerken sınıflar otomatik oluşur veya buradan ekleyebilirsiniz." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {classes.map((c) => (
            <Card key={c.id} className="p-5 bg-card border-border" data-testid={`class-card-${c.name}`}>
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-lg bg-primary/10 text-primary grid place-items-center font-extrabold">{c.name}</div>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive" data-testid={`class-delete-${c.name}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent className="bg-card border-border">
                    <AlertDialogHeader><AlertDialogTitle>{c.name} silinsin mi?</AlertDialogTitle><AlertDialogDescription>Sınıfta öğrenci varsa önce taşınmalı veya silinmelidir.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Vazgeç</AlertDialogCancel><AlertDialogAction onClick={() => del(c.id)} className="bg-destructive text-destructive-foreground">Sil</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm"><Users className="h-4 w-4 text-muted-foreground" /><span className="font-bold">{c.student_count}</span> öğrenci</div>
              {c.advisor && <div className="text-xs text-muted-foreground mt-1">Rehber: {c.advisor}</div>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
